import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Globe, KeyRound, RefreshCw, Trash2 } from 'lucide-react';
import { countryLabel } from '@/components/CountryGateNotice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Country gate admin view (17 Oct 2026): waitlist counts by country + entries (em: reference, never an address) with delete,
// and single-use expiring invite codes (shown once on creation; only a hash is stored).
export const CountryGateCard = ({ token }) => {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [wait, setWait] = useState(null);
  const [inv, setInv] = useState(null);
  const [days, setDays] = useState(14);
  const [note, setNote] = useState('');
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [w, i] = await Promise.all([axios.get(`${API_URL}/api/admin/waitlist`, H), axios.get(`${API_URL}/api/admin/invites`, H)]);
      setWait(w.data); setInv(i.data);
    } catch (e) { toast.error('Could not load the country gate data'); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeEntry = async (id) => {
    if (!window.confirm('Delete this waitlist entry? This cannot be undone.')) return;
    try { await axios.delete(`${API_URL}/api/admin/waitlist/${id}`, H); toast.success('Waitlist entry deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete refused'); }
  };
  const create = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API_URL}/api/admin/invites`, { expires_in_days: Number(days) || 14, note }, H); setFresh(r.data); setNote(''); toast.success('Invite code created - copy it now, it is shown only once'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not create the invite'); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    try { await axios.delete(`${API_URL}/api/admin/invites/${id}`, H); toast.success('Invite revoked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Revoke refused'); }
  };

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 space-y-5" data-testid="country-gate-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2"><Globe className="w-4 h-4" /> Country gate — waitlist & invites</h3>
        <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={load} data-testid="country-gate-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
      </div>

      <div data-testid="waitlist-section">
        <p className="text-sm font-medium">Waitlist: <span data-testid="waitlist-total">{wait?.total ?? '—'}</span> {wait?.by_country?.length ? '· ' + wait.by_country.map((c) => `${countryLabel(c.country)} (${c.country}) ${c.count}`).join(' · ') : ''}</p>
        <p className="text-xs text-muted-foreground mt-1">Stored: email, 2-letter country code, date. No emails are sent from the waitlist.</p>
        {wait?.entries?.length > 0 && (
          <ul className="mt-2 text-xs space-y-1 max-h-48 overflow-y-auto" data-testid="waitlist-entries">
            {wait.entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-2 rounded border border-border/40 px-2 py-1" data-testid={`waitlist-entry-${e.id.slice(0, 8)}`}>
                <span className="font-mono">{e.id.slice(0, 8)} · {e.country} · {String(e.created_at).slice(0, 10)} · {e.ref}</span>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-700" onClick={() => removeEntry(e.id)} data-testid={`waitlist-delete-${e.id.slice(0, 8)}`}><Trash2 className="w-3 h-3" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div data-testid="invites-section">
        <p className="text-sm font-medium flex items-center gap-2"><KeyRound className="w-4 h-4" /> Invite codes · mode {inv?.invite_only ? 'ON (INVITE_ONLY=true)' : 'off'} · active <span data-testid="invites-active">{inv?.active ?? '—'}</span></p>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className="h-8 w-24 text-xs" data-testid="invite-days-input" />
          <span className="text-xs text-muted-foreground">days valid</span>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="note (optional, e.g. who it is for)" className="h-8 w-64 text-xs" data-testid="invite-note-input" />
          <Button size="sm" className="rounded-full h-8 text-xs" disabled={busy} onClick={create} data-testid="invite-create-button">Create invite code</Button>
        </div>
        {fresh && (
          <p className="mt-2 text-sm rounded-lg border border-green-300 bg-green-50 p-2" data-testid="invite-fresh-code">New code: <span className="font-mono font-bold tracking-widest">{fresh.code}</span> · expires {String(fresh.expires_at).slice(0, 10)} — copy it now; it will not be shown again.</p>
        )}
        {inv?.invites?.length > 0 && (
          <ul className="mt-2 text-xs space-y-1 max-h-48 overflow-y-auto" data-testid="invites-list">
            {inv.invites.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-2 rounded border border-border/40 px-2 py-1" data-testid={`invite-${i.id.slice(0, 8)}`}>
                <span className="font-mono">{i.code_prefix}-…  · {i.status} · expires {String(i.expires_at).slice(0, 10)}{i.used_by ? ` · used by ${i.used_by}` : ''}{i.note ? ` · ${i.note}` : ''}</span>
                {i.status === 'active' && <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-700" onClick={() => revoke(i.id)} data-testid={`invite-revoke-${i.id.slice(0, 8)}`}>Revoke</Button>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
